import {
  Breadcrumbs,
  DescriptionList,
  type DescriptionListItem,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useCompensationVariantPayment,
  useEmployee,
} from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import type { CompensationVariantPayment } from '@omnipresentgroup/types';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ADDITIONAL_PAY_PATH } from '../../../constants';
import { AdditionalPayDetailCard } from './components';
import { RENDERER_MAPPING, VARIANT_PAYMENT_ENTRIES } from './constants';

type PageParameters = {
  variantPaymentId: string;
};

const getVariantPaymentDetails = (
  variantPayment: CompensationVariantPayment,
  isLoading: boolean,
): DescriptionListItem[] =>
  VARIANT_PAYMENT_ENTRIES.map(({ label, key }) => ({
    header: label,
    detail: isLoading ? (
      <Skeleton key={key} />
    ) : // eslint-disable-next-line unicorn/no-nested-ternary
    typeof RENDERER_MAPPING[key] === 'function' ? (
      RENDERER_MAPPING?.[key]?.(variantPayment)
    ) : (
      (variantPayment?.[key as keyof CompensationVariantPayment] as
        | string
        | JSX.Element)
    ),
  }));

export const AdditionalPayDetails = () => {
  const { variantPaymentId } = useParams<PageParameters>();

  const navigate = useNavigate();

  const { data: variantPayment, isLoading: isLoadingVariantPayment } =
    useCompensationVariantPayment({
      id: variantPaymentId!,
      enabled: !!variantPaymentId,
    });

  const variantPaymentName = useMemo(
    () => variantPayment?.name || toSentenceCase(variantPayment?.type || ''),
    [variantPayment],
  );

  const variantPaymentDetails = useMemo(
    () =>
      getVariantPaymentDetails(
        variantPayment as CompensationVariantPayment,
        isLoadingVariantPayment,
      ),
    [isLoadingVariantPayment, variantPayment],
  );

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: variantPayment?.employeeId,
    enabled: !!variantPayment?.employeeId,
  });

  const employeeName = useMemo(
    () =>
      employee
        ? `${employee?.personalDetails?.firstName} ${employee?.personalDetails?.lastName}`
        : '-',
    [employee],
  );

  const PageHeader = () => {
    return (
      <Stack gap="0">
        <Inline justify="space-between" minH={40} align="center">
          <Inline gap="8" align="center">
            <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
              {isLoadingVariantPayment ? (
                <Skeleton width={300} />
              ) : (
                variantPaymentName
              )}
            </Typography>
          </Inline>
        </Inline>
        <Typography as="span" size="14" color="subtle">
          {isLoadingEmployee ? <Skeleton width={200} /> : employeeName}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'additional-pay',
              title: 'Additional pay',
              onClick: () => navigate(ADDITIONAL_PAY_PATH),
            },
            {
              id: 'additional-pay-details',
              title: isLoadingVariantPayment ? '-' : variantPaymentName,
              active: true,
            },
          ]}
        />
        <PageHeader />
      </Stack>
      <Inline justify="center">
        <Stack gap="24" maxW={600} w="stretch">
          <DescriptionList
            fraction="1/2"
            id="variant-payment-details-list"
            list={variantPaymentDetails}
          />
          <AdditionalPayDetailCard
            id="variant-payment-reason"
            label="Reason"
            value={variantPayment?.reason}
            isLoading={isLoadingVariantPayment}
          />
          <AdditionalPayDetailCard
            id="variant-payment-additional-notes"
            label="Additional notes"
            value={variantPayment?.additionalNotes}
            isLoading={isLoadingVariantPayment}
          />
        </Stack>
      </Inline>
    </>
  );
};
